import React, { useEffect, useState, useRef } from 'react';
import { isNativeApp } from '../utils/platform';
import './InstallPrompt.css';

function isCapacitorNativeRuntime() {
  return (
    isNativeApp() ||
    window.location.protocol === 'capacitor:' ||
    !!window.webkit?.messageHandlers?.bridge
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isIOSRef = useRef(false);

  useEffect(() => {
    if (isCapacitorNativeRuntime()) return;

    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone;

    if (isInStandaloneMode) {
      localStorage.removeItem('pwa-install-dismissed');
      return;
    }

    const isIOS =
      /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) &&
      !window.navigator.standalone;
    isIOSRef.current = isIOS;

    if (isIOS) {
      if (localStorage.getItem('pwa-install-dismissed')) return;
      setShowIOSPrompt(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setDeferredPrompt(null);
      setDismissed(true);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  function dismiss() {
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
    if (isIOSRef.current) {
      localStorage.setItem('pwa-install-dismissed', '1');
    }
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDismissed(true);
    }
    setDeferredPrompt(null);
  }

  if (dismissed) return null;
  if (isCapacitorNativeRuntime()) return null;
  if (!deferredPrompt && !showIOSPrompt) return null;

  return (
    <div className="install-prompt">
      <div className="install-prompt-content">
        <div className="install-prompt-text">
          <strong>Install St Barb Kitchen</strong>
          {showIOSPrompt ? (
            <span>
              Tap the share button <span className="install-share-icon">&#x2191;</span> then "Add to Home Screen"
            </span>
          ) : (
            <span>Add to your home screen for quick access</span>
          )}
        </div>
        <div className="install-prompt-actions">
          {deferredPrompt && (
            <button className="btn btn-sm btn-primary" onClick={handleInstall}>
              Install
            </button>
          )}
          <button className="btn btn-sm btn-outline install-dismiss" onClick={dismiss}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
